import { computed, ref } from 'vue';
import { useAiChatWidget } from '../composables/useAiChatWidget';
import { useContactFormGroupsMode } from '../composables/useContactFormGroupsMode';
import {
  fetchPublicGroups,
  fetchPublicGroupTopics,
  fetchPublicSecondaryOrgTopics,
  isForbiddenError,
} from '../helpers/api/taskManagementPublic';
import { useMainStore } from '../stores/mainStore';
import { useTranslationStore } from '../stores/translationStore';

interface Topic {
  id: number;
  name: string;
}

interface GlobalsBranding {
  welcome_message?: string;
  chatbot_name?: string;
  photo?: { url?: string };
  color?: { hex?: string };
  bubble_message?: string;
  bubble_message_enabled?: boolean;
  icon_size?: string;
  icon_position?: string;
  show_avatar_on_bubble?: boolean;
}

interface Globals {
  favicon?: { url?: string };
  logo?: { url?: string };
  watermark?: unknown;
  info?: {
    school_name?: string;
    primary_color?: { hex?: string };
    [key: string]: unknown;
  };
  chat_configuration?: {
    branding?: GlobalsBranding;
    prompts?: unknown[];
    [key: string]: unknown;
  };
  contact_form_configuration?: {
    topics?: Topic[];
    default_email?: string;
    published?: boolean;
  } | null;
  [key: string]: unknown;
}

export const useAiChatClientInfo = () => {
  const mainStore = useMainStore();
  const translationStore = useTranslationStore();
  const { $t } = useNuxtApp();
  const { aiChatEnabled, tasksEnabled, displayChat } = useAiChatWidget();
  const { useCustomGroups, setCustomGroupsEnabled, customGroupsEnabled } = useContactFormGroupsMode();

  const language = computed(() => translationStore.locale);
  const languages = computed(() => translationStore.languages);
  const staticOrganizations = computed(() => mainStore.organizations);
  const globals = computed(() => mainStore.globals as unknown as Globals);
  const schoolName = computed(() => globals.value.info?.school_name);

  const chatConfigurationDefaults = computed(() => ({
    welcomeMessage: $t('chatAndContact.drawer.chat.defaultWelcomeMessage', {
      orgName: globals.value.info?.school_name || '',
    }),
    bubbleMessage: $t('chatAndContact.drawer.chatBubble.defaultBubbleMessage', {
      orgName: globals.value.info?.school_name || '',
    }),
    chatbotName: $t('chatAndContact.drawer.chat.defaultChatbotName'),
    avatarUrl: globals.value.logo?.url || new URL('../assets/sparkle_avatar_lg.svg', import.meta.url).href,
    color: globals.value.info?.primary_color?.hex || '#662E80',
  }));

  const chatConfiguration = computed(() => {
    const chatConfiguration = globals.value.chat_configuration || {};

    return {
      ...chatConfiguration,
      branding: {
        welcome_message: chatConfiguration?.branding?.welcome_message || chatConfigurationDefaults.value.welcomeMessage,
        chatbot_name: chatConfiguration?.branding?.chatbot_name || chatConfigurationDefaults.value.chatbotName,
        photo: { url: chatConfiguration?.branding?.photo?.url || chatConfigurationDefaults.value.avatarUrl },
        color: { hex: chatConfiguration?.branding?.color?.hex || chatConfigurationDefaults.value.color },
        bubble_message: chatConfiguration?.branding?.bubble_message || chatConfigurationDefaults.value.bubbleMessage,
        bubble_message_enabled: chatConfiguration?.branding?.bubble_message_enabled,
        show_avatar_on_bubble: chatConfiguration?.branding?.show_avatar_on_bubble,
        icon_size: chatConfiguration?.branding?.icon_size,
        icon_position: chatConfiguration?.branding?.icon_position,
      },
      prompts: chatConfiguration?.prompts || [],
    };
  });

  const buttonsConfiguration = computed(() => {
    const buttons = [
      {
        name: 'inbox',
        enabled: tasksEnabled.value && contactFormConfiguration.value?.published,
      },
      {
        name: 'ai-chat',
        enabled: aiChatEnabled.value && displayChat.value,
      },
    ];
    return {
      enabled: buttons.filter((button) => button.enabled).map((button) => button.name),
    };
  });

  const fetchedGroups = ref<Topic[]>([]);
  const fetchedTopics = ref<Topic[]>([]);
  const isLoadingOrganizations = ref(false);
  const isLoadingTopics = ref(false);
  const organizationsError = ref(false);
  const topicsError = ref(false);

  const contactFormOrganizations = computed(() =>
    useCustomGroups.value ? fetchedGroups.value : staticOrganizations.value,
  );

  /**
   * Fetches Task Management groups for the Contact Form organization dropdown.
   * Falls back to legacy mode when the feature flag is off or the API returns 403.
   */
  const fetchOrganizations = async () => {
    const parentOrgId = mainStore.parentOrgId;
    if (!parentOrgId) return;

    const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;
    if (!writableApi) return;

    if (customGroupsEnabled.value === false) {
      return;
    }

    isLoadingOrganizations.value = true;
    organizationsError.value = false;

    try {
      fetchedGroups.value = await fetchPublicGroups(writableApi, parentOrgId);
      setCustomGroupsEnabled(true);
    } catch (error) {
      if (isForbiddenError(error)) {
        setCustomGroupsEnabled(false);
        fetchedGroups.value = [];
      } else {
        organizationsError.value = true;
        console.error('Failed to fetch contact form groups:', error);
      }
    } finally {
      isLoadingOrganizations.value = false;
    }
  };

  /**
   * Fetches contact form topics for the selected organization or group.
   */
  const fetchTopics = async (orgOrGroupId: string | number) => {
    if (!orgOrGroupId) return;

    const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;
    if (!writableApi) return;

    isLoadingTopics.value = true;
    topicsError.value = false;

    try {
      if (useCustomGroups.value) {
        fetchedTopics.value = await fetchPublicGroupTopics(writableApi, orgOrGroupId);
      } else {
        fetchedTopics.value = await fetchPublicSecondaryOrgTopics(writableApi, orgOrGroupId);
      }
    } catch (error) {
      console.error('Failed to fetch contact form topics:', error);
      fetchedTopics.value = [];
      topicsError.value = true;
    } finally {
      isLoadingTopics.value = false;
    }
  };

  const contactFormTopics = computed(() => fetchedTopics.value);

  const contactFormConfiguration = computed(() => {
    const { contact_form_configuration } = globals.value;

    const baseConfig = contact_form_configuration
      ? { ...contact_form_configuration }
      : {
          default_email: '',
          published: false,
        };

    return {
      ...baseConfig,
      topics: contactFormTopics.value,
      topicsRequireOrganization: useCustomGroups.value,
      isLoadingOrganizations: isLoadingOrganizations.value,
      isLoadingTopics: isLoadingTopics.value,
      organizationsError: organizationsError.value,
      topicsError: topicsError.value,
    };
  });

  const clientInfo = computed(() => {
    return {
      organizations: contactFormOrganizations.value,
      topics: contactFormTopics.value,
      chatConfiguration: chatConfiguration.value,
      contactFormConfiguration: contactFormConfiguration.value,
      buttonsConfiguration: buttonsConfiguration.value,
      language: language.value,
      languages: languages.value,
      currentOrgName: schoolName.value,
    };
  });

  return {
    chatConfiguration,
    contactFormConfiguration,
    contactFormTopics,
    buttonsConfiguration,
    clientInfo,
    useCustomGroups,
    fetchOrganizations,
    fetchTopics,
  };
};
